import { Icon } from '@/components/shared/display/Icon';
import { Badge } from '@/components/ui/badge';
import { HEALTH_STATUS, REGULAR_DATE_FORMAT } from '@/const';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { DeepPartial, Fault } from '@/types/models/faults.types';
import { ReactNode, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { EvidenceImageModal } from './EvidenceImageModal';
import { HealthStatus } from '@/components/pages/AssetsPreviewPage/shared/AssetHealthStatusIndicator';
import ChevronRightIcon from '@/assets/Icons/outlined/chevron-right-small.svg?react';
import { BaseButton } from '@/components/uiKit/Button/BaseButton';
import { ASSET_STUDIO, FILTER_PARAMS, SENSOR_DATA_INNER_TABS } from '@/types/shared/navigation-tabs.enums';
import { useQueryNavigation } from '@/hooks/shared';
import { navigateToAssetComponents } from '@/utils/shared';

export type DrawerView = { type: 'LIST' } | { type: 'DETAIL'; itemId: string };

interface ItemsDrawerContentProps {
  items: Fault[];
  drawerView: DrawerView | null;
  onOpenDetail: (itemId: string) => void;
  //   showBreadcrumb: boolean;
  isLoading?: boolean;
  // Control which links are shown in ItemDetailsHeader
  showAssetLink?: boolean;
  showComponentLink?: boolean;
  showViewDataLink?: boolean;
  // Function to check if a component has camera
  checkHasCamera?: (assetId?: string, componentId?: string) => boolean;
}

export const ItemsDrawerContent = ({
  items,
  drawerView,
  onOpenDetail,
  isLoading,
  showAssetLink = true,
  showComponentLink = true,
  showViewDataLink = true,
  checkHasCamera,
}: //   showBreadcrumb,
ItemsDrawerContentProps) => {
  const itemsMap = useMemo(() => new Map(items.map((i) => [i.fault_id, i])), [items]);

  if (!drawerView) return null;

  // Show loading state if data is still being fetched
  if (isLoading) {
    return <div className="p-4 text-center text-muted-foreground">Loading...</div>;
  }

  switch (drawerView.type) {
    case 'LIST':
      return (
        <ItemsList
          items={items}
          onItemClick={(item) => onOpenDetail(item.fault_id)}
          showAssetLink={showAssetLink}
          showComponentLink={showComponentLink}
          showViewDataLink={showViewDataLink}
          checkHasCamera={checkHasCamera}
        />
      );

    case 'DETAIL': {
      const item = itemsMap.get(drawerView.itemId);

      if (!item) {
        return <div className="p-4 text-center text-muted-foreground">Item not found</div>;
      }

      return (
        <div className="flex flex-col gap-4">
          {/* {showBreadcrumb && <DrawerBreadcrumb onBack={onOpenList} />} */}
          <ItemDetails
            item={item}
            showAssetLink={showAssetLink}
            showComponentLink={showComponentLink}
            showViewDataLink={showViewDataLink}
            checkHasCamera={checkHasCamera}
          />
        </div>
      );
    }
    default:
      return null;
  }
};

interface DrawerBreadcrumbProps {
  onBack: () => void;
}

export const DrawerBreadcrumb = ({ onBack }: DrawerBreadcrumbProps) => {
  return (
    <button onClick={onBack} className="text-sm text-muted-foreground hover:underline w-fit">
      ← Items
    </button>
  );
};

interface ItemsListProps {
  items: Fault[];
  onItemClick: (item: Fault) => void;
  showAssetLink?: boolean;
  showComponentLink?: boolean;
  showViewDataLink?: boolean;
  checkHasCamera?: (assetId?: string, componentId?: string) => boolean;
}

const ItemsList = ({
  items,
  onItemClick,
  showAssetLink = true,
  showComponentLink = true,
  showViewDataLink = true,
  checkHasCamera,
}: ItemsListProps) => {
  return (
    <ul>
      {items.map((item) => (
        <li key={item.fault_id} onClick={() => onItemClick(item)}>
          <div className="p-6 border-b border-neutral-100 hover:bg-muted cursor-pointer">
            <ItemDetailsHeader
              item={item}
              showAssetLink={showAssetLink}
              showComponentLink={showComponentLink}
              showViewDataLink={showViewDataLink}
              hasCamera={checkHasCamera?.(item.location?.asset_id, item.location?.component_id)}
              rightElement={<DiagnosticsChevron />}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

interface ItemDetailsProps {
  item: DeepPartial<Fault>;
  showAssetLink?: boolean;
  showComponentLink?: boolean;
  showViewDataLink?: boolean;
  checkHasCamera?: (assetId?: string, componentId?: string) => boolean;
}

const ItemDetails = ({
  item,
  showAssetLink = true,
  showComponentLink = true,
  showViewDataLink = true,
  checkHasCamera,
}: ItemDetailsProps) => {
  const hasCamera = checkHasCamera?.(item.location?.asset_id, item.location?.component_id);

  return (
    <div>
      <div className="p-6 border-b border-neutral-100">
        <ItemDetailsHeader
          item={item}
          showAssetLink={showAssetLink}
          showComponentLink={showComponentLink}
          showViewDataLink={showViewDataLink}
          hasCamera={hasCamera}
        />
      </div>

      <ItemDetailsContent item={item} showViewDataLink={showViewDataLink} hasCamera={hasCamera} />
    </div>
  );
};

interface ItemDetailsHeaderProps {
  item: DeepPartial<Fault>;
  rightElement?: ReactNode;
  // Control which links are shown/clickable
  showAssetLink?: boolean; // default: true
  showComponentLink?: boolean; // default: true
  showViewDataLink?: boolean; // default: true
  hasCamera?: boolean; // if true, shows "Go to Visual AI" instead of "Go to Data Studio"
}

export const ItemDetailsHeader = ({
  item,
  rightElement,
  showAssetLink = true,
  showComponentLink = true,
  showViewDataLink = true,
  hasCamera = false,
}: ItemDetailsHeaderProps) => {
  const { siteId } = useParams();
  const { navigateWithQuery } = useQueryNavigation();

  const handleAssetClick = (e: React.MouseEvent) => {
    if (!showAssetLink) return;
    e.stopPropagation();
    const assetId = item?.location?.asset_id;
    if (siteId && assetId) {
      navigateToAssetComponents({
        siteId,
        assetId,
        navigateWithQuery,
        replaceParams: {
          drawer: null,
          itemId: null,
          [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS,
          [FILTER_PARAMS.TAB]: null,
        },
      });
    }
  };

  const handleComponentClick = (e: React.MouseEvent) => {
    if (!showComponentLink) return;
    e.stopPropagation();
    const assetId = item?.location?.asset_id;
    const componentId = item?.location?.component_id;
    if (siteId && assetId && componentId) {
      navigateToAssetComponents({
        siteId,
        assetId,
        componentId,
        navigateWithQuery,
        replaceParams: {
          drawer: null,
          itemId: null,
          [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS,
          [FILTER_PARAMS.TAB]: null,
        },
      });
    }
  };

  const handleViewData = (e: React.MouseEvent) => {
    if (!showViewDataLink) return;
    e.stopPropagation();
    const assetId = item?.location?.asset_id;
    const componentId = item?.location?.component_id;
    const evidenceResourceId = item?.evidence?.evidence_data?.find((e: any) => e?.resource_id && e?.source_type?.toLowerCase() === 'camera')?.resource_id;
    if (siteId && assetId && componentId) {
      navigateToAssetComponents({
        siteId,
        assetId,
        componentId,
        navigateWithQuery,
        replaceParams: { drawer: null, itemId: null },
        addParams: hasCamera
          ? {
              [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.VISUAL_AI,
              ...(evidenceResourceId && { [FILTER_PARAMS.EVIDENCE_RESOURCE_ID]: evidenceResourceId }),
            }
          : {
              [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
              [FILTER_PARAMS.TAB]: SENSOR_DATA_INNER_TABS.SENSOR_FUSION,
            },
      });
    }
  };

  // Determine if asset/component should be links or plain text
  const linkClassName = 'cursor-pointer hover:underline text-primary-600';
  const textClassName = 'font-medium';

  return (
    <div className="flex justify-between">
      <div className="flex flex-col gap-2 text-lg">
        <div className="flex items-center gap-2 font-semibold">
          <div>{item?.fault_type?.name}</div>
          <HealthStatus healthStatus={item.health_status as HEALTH_STATUS} className="text-sm" />
        </div>
        <div className="flex items-center gap-2 font-semibold text-base">
          <div
            className={showAssetLink ? linkClassName : textClassName}
            onClick={showAssetLink ? handleAssetClick : undefined}
          >
            {item?.location?.asset_ui_name},
          </div>
          <div
            className={showComponentLink ? linkClassName : textClassName}
            onClick={showComponentLink ? handleComponentClick : undefined}
          >
            {item?.location?.component_ui_name}
          </div>
          {showViewDataLink && (
            <>
              <span className="inline-block w-px h-3 bg-neutral-100 mx-2" />
              <div className="cursor-pointer hover:underline text-primary-600" onClick={handleViewData}>
                {hasCamera ? 'Go to Visual AI' : 'Go to Data Studio'}
              </div>
            </>
          )}
        </div>
        {('timing' in item || 'verification_date' in item) && (
          <div className="text-subtext text-sm text-start">
            {'timing' in item && (
              <>
                <span>
                  First Diagnosed:{' '}
                  {item.timing?.start_date ? format(new Date(item.timing.start_date), REGULAR_DATE_FORMAT) : 'N/A'}
                </span>
                <span className="inline-block w-px h-3 bg-neutral-100 mx-2" />
                <span>
                  Days Open:
                  {item.timing?.days_open
                    ? ` ${item.timing.days_open} ${item.timing.days_open === 1 ? 'day' : 'days'}`
                    : ' N/A'}
                </span>
              </>
            )}
            {/* {'verification_date' in item && ( */}
            <>
              {'timing' in item && <span className="inline-block w-px h-3 bg-neutral-200 mx-2" />}
              <span>
                Verified:{' '}
                {/* //currently we use the timing.validation_date until verification_date will have data to return  */}
                {item.timing?.validation_date
                  ? format(new Date(item.timing.validation_date), REGULAR_DATE_FORMAT)
                  : ' N/A'}
              </span>
            </>
            {/* )} */}
          </div>
        )}
      </div>
      {rightElement}
    </div>
  );
};

interface EvidenceLinkProps {
  resourceId?: string | null;
  evidenceText?: string;
  hasCamera?: boolean;
  assetId?: string;
  componentId?: string;
  showGoToDataStudio?: boolean;
  onNavigate: () => void;
  faultTypeName?: string;
}

const EvidenceLink = ({
  resourceId,
  evidenceText,
  hasCamera,
  assetId,
  componentId,
  showGoToDataStudio,
  onNavigate,
  faultTypeName,
}: EvidenceLinkProps) => {
  const isDefect = faultTypeName?.toLowerCase()?.includes('defect');
  const linkText = isDefect ? 'View Defect' : hasCamera && resourceId ? 'View video' : 'View chart';

  if (!resourceId) {
    return (
      <div
        className="cursor-pointer hover:underline text-primary-600 font-semibold text-base whitespace-nowrap"
        onClick={onNavigate}
      >
        {linkText}
      </div>
    );
  }

  return (
    <BaseButton
      dialog={{
        title: 'Evidence View',
        element: (
          <EvidenceImageModal
            resourceId={resourceId}
            evidenceText={evidenceText}
            assetId={assetId}
            componentId={componentId}
            showGoToDataStudio={showGoToDataStudio}
            hasCamera={hasCamera}
          />
        ),
        className: 'w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh] mt-0 flex flex-col',
      }}
      className="text-primary-600 font-semibold text-base whitespace-nowrap h-6 p-0 hover:underline"
    >
      {linkText}
    </BaseButton>
  );
};

interface ItemDetailsContentProps {
  item: DeepPartial<Fault>;
  showViewDataLink?: boolean;
  hasCamera?: boolean;
}

export const ItemDetailsContent = ({ item, showViewDataLink = true, hasCamera }: ItemDetailsContentProps) => {
  const { siteId } = useParams();
  const { navigateWithQuery } = useQueryNavigation();

  const hasCausesOrActions =
    !!item?.evidence?.possible_causes?.length || !!item?.evidence?.prescriptive_actions?.length;

  const handleNavigateToView = () => {
    const assetId = item?.location?.asset_id;
    const componentId = item?.location?.component_id;
    const evidenceResourceId = item?.evidence?.evidence_data?.find((e: any) => e?.resource_id && e?.source_type?.toLowerCase() === 'camera')?.resource_id;
    if (siteId && assetId && componentId) {
      navigateToAssetComponents({
        siteId,
        assetId,
        componentId,
        navigateWithQuery,
        replaceParams: { drawer: null, itemId: null },
        addParams: hasCamera
          ? {
              [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.VISUAL_AI,
              ...(evidenceResourceId && { [FILTER_PARAMS.EVIDENCE_RESOURCE_ID]: evidenceResourceId }),
            }
          : {
              [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
              [FILTER_PARAMS.TAB]: SENSOR_DATA_INNER_TABS.SENSOR_FUSION,
            },
      });
    }
  };

  return (
    <div className="gap-2 flex flex-col text-base">
      <div className="flex gap-2 p-6 border-b border-neutral-100">
        <div className="w-32">Work Order</div>
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col w-full">
            {item?.work_orders?.length ? (
              <>
                {item.work_orders?.map((wo, index) => (
                  <div
                    key={wo?.wo_id}
                    className="flex  items-center gap-4 py-3 first:py-0 first:pb-3 border-b last:border-none last:pb-0 border-neutral-50 w-full"
                  >
                    <Badge variant="secondary" className={'bg-neutral-50 hover:bg-neutral-50'}>
                      WO-0{index + 1}
                    </Badge>
                    <div>{wo?.wo_reference}</div>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-neutral-200">Work order not assigned</div>
            )}
          </div>
          {/* currently hide until phase 2
          <div className="flex gap-2">
            <Icon icon={TrashIcon} />
            <Icon icon={PenIcon} />
          </div> */}
        </div>
      </div>

      <div className="flex px-6 py-4 flex-col gap-2">
        <div className="flex gap-2">
          <div className="w-32 pt-3">Evidence</div>
          <div className="flex flex-col w-full">
            {item?.evidence?.evidence_data?.length ? (
              item.evidence.evidence_data.map((evidence, index) => {
                const hasText = !!evidence?.text;
                const hasResourceId = !!evidence?.resource_id;
                const isCameraEvidence = (evidence as any)?.source_type?.toLowerCase() === 'camera';
                // Show link for camera evidence when component has camera, otherwise for first item
                const showLink = hasCamera
                  ? isCameraEvidence && hasResourceId
                  : index === 0 && (hasResourceId || !hasText);
                return (
                  <div
                    key={index}
                    className={`flex justify-start w-full py-3 place-items-center border-b border-neutral-50 ${
                      !hasCausesOrActions ? 'last:border-b-0' : ''
                    }`}
                  >
                    {hasText && <div>{evidence.text}</div>}
                    {showLink && hasText && <div className=" w-px h-3 bg-neutral-100 mx-2" />}
                    {showLink && (
                      <EvidenceLink
                        resourceId={evidence?.resource_id}
                        evidenceText={evidence?.text}
                        hasCamera={hasCamera}
                        assetId={item?.location?.asset_id}
                        componentId={item?.location?.component_id}
                        showGoToDataStudio={showViewDataLink}
                        onNavigate={handleNavigateToView}
                        faultTypeName={item?.fault_type?.name}
                      />
                    )}
                  </div>
                );
              })
            ) : (
              <div className={`py-3 ${hasCausesOrActions ? 'border-b border-neutral-50' : ''}`}>
                <EvidenceLink
                  hasCamera={hasCamera}
                  assetId={item?.location?.asset_id}
                  componentId={item?.location?.component_id}
                  showGoToDataStudio={showViewDataLink}
                  onNavigate={handleNavigateToView}
                  faultTypeName={item?.fault_type?.name}
                />
              </div>
            )}
          </div>
        </div>

        {item?.evidence?.possible_causes?.length ? (
          <div className="flex gap-2">
            <div className="w-32 pt-3">Causes</div>
            <div className="flex flex-col w-full">
              {item.evidence.possible_causes.map((cause, index) => (
                <div key={index} className="py-3 border-b border-neutral-50">
                  {cause?.trim()}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {item?.evidence?.prescriptive_actions?.length ? (
          <div className="flex gap-2">
            <div className="w-32 pt-3">Actions</div>
            <div className="flex flex-col w-full">
              {item.evidence.prescriptive_actions.map((action, index) => (
                <div key={index} className="py-3 [&:not(:last-child)]:border-b border-neutral-50">
                  {action?.trim()}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const DiagnosticsChevron = () => <Icon icon={ChevronRightIcon} className="mx-6 self-start" />;
